import { useEffect, useRef } from "react";
import { getUnreadCount } from "../Apis/ChatApis";

const useUnreadSocket = ({
  onUpdate,
  onNewMessage,
  onMarkRead,
  onEditMessage,
  onDeleteMessage
}) => {
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const onUpdateRef = useRef(onUpdate);
  const onNewMessageRef = useRef(onNewMessage);
  const onMarkReadRef = useRef(onMarkRead);
  const onEditMessageRef = useRef(onEditMessage);
  const onDeleteMessageRef = useRef(onDeleteMessage);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  useEffect(() => {
    onMarkReadRef.current = onMarkRead;
  }, [onMarkRead]);

  useEffect(() => {
    onEditMessageRef.current = onEditMessage;
  }, [onEditMessage]);

  useEffect(() => {
    onDeleteMessageRef.current = onDeleteMessage;
  }, [onDeleteMessage]);

  useEffect(() => {
    let isMounted = true;
    
    const connect = () => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        return;
      }

      const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL;
      const socket = new WebSocket(`${WS_BASE_URL}/chat/`);

      socket.onopen = async () => {
        if (!isMounted) return;
        console.log("✅ WebSocket Connected");
        if (onUpdateRef.current) {
          try {
            const res = await getUnreadCount();
            if (!res.error && onUpdateRef.current) {
              onUpdateRef.current({
                groupUnread: res.group_unread,
                personalUnread: res.personal_unread,
                totalUnread: res.total_unread,
                unreadBySender: res.unread_by_user || []
              });
            }
          } catch (err) {
            console.log("Failed to fetch initial unread:", err);
          }
        }
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("WS DATA:", data.type, data);

          switch (data.type) {
            case "new_message":
              if (onNewMessageRef.current) {
                onNewMessageRef.current(data.data);
              }
              break;

            case "unread_update":
              if (onUpdateRef.current) {
                onUpdateRef.current({
                  groupUnread: data.group_unread,
                  personalUnread: data.personal_unread,
                  totalUnread: data.total_unread,
                  unreadBySender: data.unread_by_sender || []
                });
              }
              break;

            case "mark_read":
              if (onMarkReadRef.current) {
                onMarkReadRef.current(data.message_ids);
              }
              break;

            case "edit_message":
              if (onEditMessageRef.current) {
                onEditMessageRef.current(data.data);
              }
              break;

            case "delete_message":
              if (onDeleteMessageRef.current) {
                onDeleteMessageRef.current(data.message_ids);
              }
              break;

            default:
              break;
          }
        } catch (err) {
          console.log("WS PARSE ERROR:", err);
        }
      };

      socket.onclose = () => {
        if (!isMounted) return;
        console.log("❌ Socket Closed");
        
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMounted) {
            connect();
          }
        }, 3000);
      };

      socket.onerror = (err) => {
        console.log("WS ERROR:", err);
        socket.close();
      };

      socketRef.current = socket;
    };

    connect();

    return () => {
      isMounted = false;
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []); 

  return socketRef;
};

export default useUnreadSocket;